import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch } from 'app/store';

import {
  selectCombinedEvents,
  selectInterviewSlots,
  selectInterviewSlotsApplicationId,
  selectInterviewSlotsCount,
  selectInterviewSlotsError,
  selectInterviewSlotsLoading,
  selectProposedSlots,
  selectProposedSlotsCount,
  selectSelectedSlot
} from 'features/interviewSlots/interviewSlotsSelectors';
import {
  addSlot as addSlotAction,
  clearProposedSlots as clearProposedSlotsAction,
  clearSlots as clearSlotsAction,
  initProposedSlots as initProposedSlotsAction,
  removeSlot as removeSlotAction,
  resetSlots as resetSlotsAction,
  selectProposedSlot as selectProposedSlotAction,
  setProposedSlots as setProposedSlotsAction
} from 'features/interviewSlots/interviewSlotsSlice';

import { CalendarEvent, InterviewSlotSerialized } from 'types/interviewSlots';

interface UseInterviewSlotsReturn {
  slots: InterviewSlotSerialized[];
  slotsCount: number;
  proposedSlots: InterviewSlotSerialized[];
  proposedSlotsCount: number;
  selectedSlot: InterviewSlotSerialized | null;
  events: CalendarEvent[];
  applicationId: string | null;
  isLoading: boolean;
  error: string | null;
  addSlot: (slot: InterviewSlotSerialized) => void;
  removeSlot: (slotId: number) => void;
  clearSlots: () => void;
  resetSlots: () => void;
  setProposedSlots: (slots: InterviewSlotSerialized[]) => void;
  initProposedSlots: (slots: InterviewSlotSerialized[]) => void;
  selectProposedSlot: (slot: InterviewSlotSerialized) => void;
  clearProposedSlots: () => void;
}

export const useInterviewSlots = (): UseInterviewSlotsReturn => {
  const dispatch: AppDispatch = useDispatch();

  const slots = useSelector(selectInterviewSlots);
  const slotsCount = useSelector(selectInterviewSlotsCount);
  const proposedSlots = useSelector(selectProposedSlots);
  const proposedSlotsCount = useSelector(selectProposedSlotsCount);
  const selectedSlot = useSelector(selectSelectedSlot);
  const events = useSelector(selectCombinedEvents);
  const applicationId = useSelector(selectInterviewSlotsApplicationId);
  const isLoading = useSelector(selectInterviewSlotsLoading);
  const error = useSelector(selectInterviewSlotsError);

  const addSlot = useCallback(
    (slot: InterviewSlotSerialized) => {
      dispatch(addSlotAction(slot));
    },
    [dispatch]
  );

  const removeSlot = useCallback(
    (slotId: number) => {
      dispatch(removeSlotAction(slotId));
    },
    [dispatch]
  );

  const clearSlots = useCallback(() => {
    dispatch(clearSlotsAction());
  }, [dispatch]);

  const resetSlots = useCallback(() => {
    dispatch(resetSlotsAction());
  }, [dispatch]);

  const setProposedSlots = useCallback(
    (slots: InterviewSlotSerialized[]) => {
      dispatch(setProposedSlotsAction(slots));
    },
    [dispatch]
  );

  const initProposedSlots = useCallback(
    (slots: InterviewSlotSerialized[]) => {
      dispatch(initProposedSlotsAction(slots));
    },
    [dispatch]
  );

  const selectProposedSlot = useCallback(
    (slot: InterviewSlotSerialized) => {
      dispatch(selectProposedSlotAction(slot));
    },
    [dispatch]
  );

  const clearProposedSlots = useCallback(() => {
    dispatch(clearProposedSlotsAction());
  }, [dispatch]);

  return {
    slots,
    slotsCount,
    proposedSlots,
    proposedSlotsCount,
    selectedSlot,
    events,
    applicationId,
    isLoading,
    error,
    addSlot,
    removeSlot,
    clearSlots,
    resetSlots,
    setProposedSlots,
    initProposedSlots,
    selectProposedSlot,
    clearProposedSlots
  };
};

export default useInterviewSlots;
